import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { useAuth } from "../contexts/useAuth.jsx";
import { useTranslation } from "../contexts/TranslationContext.jsx";
import { LanguageSwitcher } from "../components/LanguageSwitcher.jsx";
import { UserMenu } from "../components/UserMenu.jsx";
import { ScrollToTopButton } from "../components/ScrollToTopButton.jsx";
import { Modal } from "../components/Modal";
import { getHomePathForRole } from "../utils/roleHome";

// Link da navbar: pilula com hover na cor da marca e destaque da rota atual.
const navLinkClass = ({ isActive }) =>
  `px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
    isActive
      ? "bg-brand-500/15 text-brand-400 ring-1 ring-inset ring-brand-500/30"
      : "text-gray-300 hover:bg-white/[0.07] hover:text-brand-400"
  }`;

// Chrome do app autenticado: navbar completa, menu de usuário e modal de logout.
export function AppLayout() {
  const { t } = useTranslation();
  const { isAdmin, isPaid, logout } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const homePath = getHomePathForRole({ isAdmin, isPaid });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest("#modal-content") && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  function closeMenu() {
    setIsMenuOpen(false);
  }

  return (
    <div className="dark min-h-screen flex flex-col bg-[#0a0a0a] text-white">
      {/* Espaçador para compensar navbar fixa */}
      <div className="shrink-0 h-14" />

      <nav className="fixed top-0 left-0 right-0 z-50 flex flex-wrap items-center justify-between px-6 py-3 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-white/5">
        <Link to={homePath} className="flex items-center gap-2 shrink-0" onClick={closeMenu}>
          <span className="text-brand-500 font-bold text-xl tracking-tight">SecretDev</span>
        </Link>

        {/* Nav Links - Desktop */}
        <div className="hidden md:flex items-center gap-1">
          {isPaid && (
            <NavLink to="/dashboard" className={navLinkClass} onClick={closeMenu}>
              {t("nav.dashboard")}
            </NavLink>
          )}
          <NavLink to="/buy" className={navLinkClass} onClick={closeMenu}>
            {t("nav.buyPlans")}
          </NavLink>
          {isPaid && (
            <>
              <NavLink to="/practice" className={navLinkClass} onClick={closeMenu}>
                {t("nav.practice")}
              </NavLink>
              <NavLink to="/configure-resume" className={navLinkClass} onClick={closeMenu}>
                {t("nav.resume")}
              </NavLink>
            </>
          )}
          {isAdmin && (
            <NavLink to="/admin" className={navLinkClass} onClick={closeMenu}>
              {t("nav.admin")}
            </NavLink>
          )}
          <NavLink to="/tutorial" className={navLinkClass} onClick={closeMenu}>
            Tutorial
          </NavLink>
          {isPaid && (
            <NavLink to="/download" className={navLinkClass} onClick={closeMenu}>
              {t("nav.downloadApp")}
            </NavLink>
          )}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-3 shrink-0">
          <LanguageSwitcher />
          <UserMenu onLogout={() => setIsLogoutModalOpen(true)} />
          <button
            className="md:hidden text-gray-300 hover:text-white"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <Menu size={22} />
          </button>
        </div>
      </nav>

      {/* Mobile Drawer */}
      {isMenuOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden">
          <div className="bg-[#111111] border-r border-white/10 w-64 h-full p-6 flex flex-col gap-1" id="modal-content">
            <div className="flex items-center justify-between mb-6">
              <span className="text-brand-500 font-bold text-lg">SecretDev</span>
              <button className="text-gray-400 hover:text-white transition" onClick={closeMenu}>
                <X size={20} />
              </button>
            </div>
            {isPaid && (
              <Link to="/dashboard" className="text-gray-300 hover:text-white py-2 text-sm transition" onClick={closeMenu}>
                {t("nav.dashboard")}
              </Link>
            )}
            <Link to="/profile" className="text-gray-300 hover:text-white py-2 text-sm transition" onClick={closeMenu}>
              {t("nav.profile")}
            </Link>
            <Link to="/buy" className="text-brand-500 hover:text-brand-400 py-2 text-sm transition" onClick={closeMenu}>
              {t("nav.buyPlans")}
            </Link>
            {isPaid && (
              <>
                <Link to="/practice" className="text-gray-300 hover:text-white py-2 text-sm transition" onClick={closeMenu}>
                  {t("nav.practice")}
                </Link>
                <Link to="/configure-resume" className="text-gray-300 hover:text-white py-2 text-sm transition" onClick={closeMenu}>
                  {t("nav.resume")}
                </Link>
              </>
            )}
            {isAdmin && (
              <Link to="/admin" className="text-gray-300 hover:text-white py-2 text-sm transition" onClick={closeMenu}>
                {t("nav.admin")}
              </Link>
            )}
            <Link to="/tutorial" className="text-gray-300 hover:text-white py-2 text-sm transition" onClick={closeMenu}>
              Tutorial
            </Link>
            {isPaid && (
              <Link to="/download" className="text-emerald-400 hover:text-emerald-300 py-2 text-sm transition" onClick={closeMenu}>
                {t("nav.downloadApp")}
              </Link>
            )}
            <button
              onClick={() => {
                setIsLogoutModalOpen(true);
                closeMenu();
              }}
              className="flex items-center gap-2 text-red-400 hover:text-red-300 w-full text-left py-2 text-sm mt-4 border-t border-white/10 pt-4 transition"
            >
              <LogOut size={16} /> {t("nav.logout")}
            </button>
          </div>
        </div>
      )}

      <Outlet />

      <ScrollToTopButton />

      <Modal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={() => {
          logout();
          setIsLogoutModalOpen(false);
          navigate("/");
        }}
        message={t("logoutModal.title")}
        confirmText={t("logoutModal.confirm")}
        cancelText={t("logoutModal.cancel")}
        variant="danger"
      />
    </div>
  );
}
